// ---------------------------------------------------------------------------
// SQL Keywords for syntax highlighting
// ---------------------------------------------------------------------------
export const SQL_KEYWORDS = new Set([
  'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'IS', 'NULL', 'AS',
  'ON', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'FULL', 'CROSS', 'NATURAL',
  'ORDER', 'BY', 'GROUP', 'HAVING', 'LIMIT', 'OFFSET', 'UNION', 'ALL', 'DISTINCT',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'CREATE', 'ALTER', 'DROP',
  'TABLE', 'INDEX', 'VIEW', 'WITH', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'EXISTS', 'BETWEEN', 'LIKE', 'ASC', 'DESC', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX',
  'ROWNUM', 'FETCH', 'FIRST', 'NEXT', 'ROWS', 'ONLY', 'OVER', 'PARTITION',
  'RANK', 'ROW_NUMBER', 'DENSE_RANK', 'LAG', 'LEAD', 'NVL', 'DECODE', 'COALESCE',
  'TO_CHAR', 'TO_DATE', 'TO_NUMBER', 'SYSDATE', 'DUAL', 'TRUNC', 'SUBSTR',
]);

function escHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Returns an HTML string (not JSX) so callers can post-process it.
export function highlightSQL(text) {
  return text.split(/(\b\w+\b)/g).map(token => {
    if (SQL_KEYWORDS.has(token.toUpperCase())) {
      return `<span style="color:#5ec4c0">${escHtml(token)}</span>`;
    }
    if (/^\d+(\.\d+)?$/.test(token)) {
      return `<span style="color:#d19a66">${escHtml(token)}</span>`;
    }
    return escHtml(token);
  }).join('');
}

// ---------------------------------------------------------------------------
// SQL Formatter — production-ready Oracle / BIP formatting
// Style: keyword on its own line, 4-space indent, padded AS aliases,
//        comment groups by table alias, 1=1 WHERE prefix, trailing semicolon
// ---------------------------------------------------------------------------
export function formatSQL(raw) {
  if (typeof raw !== 'string') return '';
  if (!raw.trim()) return raw;

  // 1. Preserve string literals and comments
  const literals = [];
  let work = raw.replace(/'([^']*(?:''[^']*)*)'/g, (m) => {
    literals.push(m);
    return `__LIT${literals.length - 1}__`;
  });
  const existingComments = [];
  work = work.replace(/--(.*)/g, (m) => {
    existingComments.push(m);
    return `__CMT${existingComments.length - 1}__`;
  });

  // 2. Normalize whitespace, strip trailing semicolons
  work = work.replace(/;\s*$/, '').replace(/\s+/g, ' ').trim();

  // 3. Tokenize
  const tokens = [];
  const tokRe = /(__LIT\d+__|__CMT\d+__|\(|\)|,|<>|!=|>=|<=|=>|\|\||[<>=]|[^\s(),<>=!|]+)/g;
  let m;
  while ((m = tokRe.exec(work)) !== null) tokens.push(m[1]);

  // Helper: restore placeholders in a string
  const restore = (s) => {
    let r = s;
    literals.forEach((lit, idx) => { r = r.replace(`__LIT${idx}__`, lit); });
    existingComments.forEach((c, idx) => { r = r.replace(`__CMT${idx}__`, c); });
    return r;
  };

  // 4. Keyword sets
  const MAJOR = new Set(['SELECT', 'FROM', 'WHERE', 'SET', 'VALUES', 'HAVING', 'LIMIT', 'OFFSET', 'FETCH']);
  const MAJOR2 = new Set([
    'ORDER BY', 'GROUP BY', 'INSERT INTO', 'DELETE FROM', 'UNION ALL',
    'UNION', 'INTERSECT', 'EXCEPT', 'PARTITION BY',
  ]);
  const JOINS = new Set([
    'JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN',
    'CROSS JOIN', 'LEFT OUTER JOIN', 'RIGHT OUTER JOIN', 'FULL OUTER JOIN',
    'NATURAL JOIN',
  ]);
  const JOINS3 = new Set(['LEFT OUTER JOIN', 'RIGHT OUTER JOIN', 'FULL OUTER JOIN']);

  const peek = (idx, n) => {
    const parts = [];
    for (let j = 0; j < n && idx + j < tokens.length; j++) parts.push(tokens[idx + j].toUpperCase());
    return parts.join(' ');
  };

  // 5. Parse into clauses: { type, keyword, items[] }
  // Each item is a string of tokens for one "unit" (column, table, condition, etc.)
  const clauses = [];
  let curClause = null;
  let curItem = '';
  let depth = 0;

  const flushItem = () => {
    const trimmed = curItem.trim();
    if (trimmed && curClause) curClause.items.push(trimmed);
    curItem = '';
  };

  const startClause = (keyword, type) => {
    flushItem();
    if (curClause) clauses.push(curClause);
    curClause = { type, keyword, items: [] };
  };

  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i];
    const up = tok.toUpperCase();

    // Comments — attach to current item
    if (tok.startsWith('__CMT')) {
      curItem += (curItem ? ' ' : '') + tok;
      i++;
      continue;
    }

    // Parentheses — track depth; only split clauses at top level
    if (tok === '(') {
      depth++;
      // No space before ( if preceded by a function name
      const lastWord = curItem.match(/\w$/);
      curItem += (lastWord ? '' : (curItem ? ' ' : '')) + '(';
      i++;
      continue;
    }
    if (tok === ')') {
      depth--;
      curItem += ')';
      i++;
      continue;
    }

    // Only recognize keywords at top level (depth === 0)
    if (depth > 0) {
      // No space after (
      const endsWithOpen = curItem.endsWith('(');
      curItem += (endsWithOpen ? '' : (curItem ? ' ' : '')) + tok;
      i++;
      continue;
    }

    // 3-word joins
    if (i + 2 < tokens.length && JOINS3.has(peek(i, 3))) {
      const kw = [tokens[i], tokens[i+1], tokens[i+2]].map(t => t.toUpperCase()).join(' ');
      startClause(kw, 'JOIN');
      i += 3;
      continue;
    }

    // 2-word keywords
    if (i + 1 < tokens.length) {
      const two = peek(i, 2);
      if (MAJOR2.has(two)) {
        startClause(tokens[i].toUpperCase() + ' ' + tokens[i+1].toUpperCase(), two.startsWith('ORDER') ? 'ORDER' : two.startsWith('GROUP') ? 'GROUP' : two);
        i += 2;
        continue;
      }
      if (JOINS.has(two)) {
        startClause(tokens[i].toUpperCase() + ' ' + tokens[i+1].toUpperCase(), 'JOIN');
        i += 2;
        continue;
      }
    }

    // 1-word major
    if (MAJOR.has(up)) {
      startClause(up, up);
      i++;
      continue;
    }
    // 1-word join
    if (JOINS.has(up)) {
      startClause(up, 'JOIN');
      i++;
      continue;
    }
    // WITH
    if (up === 'WITH' && (!curClause || curClause.type !== 'FETCH')) {
      startClause('WITH', 'WITH');
      i++;
      continue;
    }

    // ON inside a JOIN — start new sub-item prefixed with ON
    if (up === 'ON' && curClause && curClause.type === 'JOIN') {
      flushItem();
      curItem = 'ON';
      i++;
      continue;
    }

    // AND / OR — in WHERE/HAVING, split items; in JOIN ON context, split items
    if ((up === 'AND' || up === 'OR') && curClause) {
      if (curClause.type === 'WHERE' || curClause.type === 'HAVING') {
        flushItem();
        curItem = up;
      } else if (curClause.type === 'JOIN') {
        flushItem();
        curItem = up;
      } else {
        curItem += ' ' + tok;
      }
      i++;
      continue;
    }

    // Commas — split items in SELECT, ORDER, GROUP
    if (tok === ',' && curClause && (curClause.type === 'SELECT' || curClause.type === 'ORDER' || curClause.type === 'GROUP')) {
      flushItem();
      i++;
      continue;
    }

    // Default: append
    curItem += (curItem ? ' ' : '') + tok;
    i++;
  }
  flushItem();
  if (curClause) clauses.push(curClause);

  // 6. Post-process SELECT columns: auto-add AS alias, group by table alias, pad
  const processSelectClause = (clause) => {
    const cols = clause.items.map(item => {
      // Uppercase keywords in the item
      let col = item.replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);

      // Check if already has AS
      const asMatch = col.match(/^(.+?)\s+AS\s+(\S+)$/i);
      if (asMatch) {
        return { expr: asMatch[1].trim(), alias: asMatch[2].trim() };
      }

      // Detect expression vs simple column
      const trimCol = col.trim();
      // Skip adding alias for *, expressions with parens that are aggregates, etc.
      if (trimCol === '*' || trimCol.endsWith('.*')) {
        return { expr: trimCol, alias: null };
      }

      // Derive alias from last segment after dot, or the whole thing
      let alias;
      const dotParts = trimCol.split('.');
      const lastPart = dotParts[dotParts.length - 1].toLowerCase();
      // Only auto-alias simple column references (no parens, no operators)
      if (/^[a-zA-Z_]\w*$/.test(lastPart)) {
        alias = lastPart;
      } else {
        alias = null;
      }
      return { expr: trimCol, alias };
    });

    // Extract table alias from expression — only simple alias.column patterns
    const getTableAlias = (expr) => {
      // Match pattern: word.word at the start (simple table alias reference)
      const m = expr.match(/^([a-zA-Z_]\w*)\.([a-zA-Z_]\w*)$/);
      return m ? m[1] : null;
    };

    // Group by table alias prefix
    const groups = [];
    let lastAlias = null;
    for (const col of cols) {
      const tblAlias = getTableAlias(col.expr);
      if (tblAlias && tblAlias.toLowerCase() !== (lastAlias || '').toLowerCase()) {
        groups.push({ comment: `-- ${tblAlias}`, cols: [] });
        lastAlias = tblAlias;
      } else if (!tblAlias && lastAlias !== '__noalias__') {
        if (groups.length === 0) groups.push({ comment: null, cols: [] });
        lastAlias = '__noalias__';
      }
      if (groups.length === 0) groups.push({ comment: null, cols: [] });
      groups[groups.length - 1].cols.push(col);
    }

    // Calculate max expression width for AS padding (only among cols that have aliases)
    const allWithAlias = cols.filter(c => c.alias);
    const maxExprLen = allWithAlias.length > 0
      ? Math.max(...allWithAlias.map(c => c.expr.length))
      : 0;

    // Build lines
    const lines = [];
    const totalCols = cols.length;
    let colIdx = 0;
    for (const group of groups) {
      if (group.comment) lines.push('    ' + group.comment);
      for (const col of group.cols) {
        let line;
        if (col.alias) {
          const padded = col.expr.padEnd(maxExprLen);
          line = '    ' + padded + ' AS ' + col.alias;
        } else {
          line = '    ' + col.expr;
        }
        colIdx++;
        if (colIdx < totalCols) line += ',';
        lines.push(line);
      }
    }
    return lines;
  };

  // 7. Process FROM clause items (tables + joins)
  const processFromClause = (fromClause, joinClauses) => {
    const lines = [];
    // FROM table(s)
    for (const item of fromClause.items) {
      const upper = item.replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);
      lines.push('    ' + upper);
    }
    // JOINs
    for (const jc of joinClauses) {
      let joinTableDone = false;
      for (const item of jc.items) {
        const upper = item.replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);
        if (!joinTableDone) {
          // First item is the table being joined
          lines.push('    ' + jc.keyword + ' ' + upper);
          joinTableDone = true;
        } else if (upper.startsWith('ON') || upper.startsWith('on')) {
          lines.push('        ' + upper);
        } else if (upper.startsWith('AND') || upper.startsWith('OR') || upper.startsWith('and') || upper.startsWith('or')) {
          lines.push('        ' + upper);
        } else {
          lines.push('        ' + upper);
        }
      }
    }
    return lines;
  };

  // 8. Process WHERE clause — add 1=1, indent conditions
  const processWhereClause = (clause) => {
    const lines = [];
    // Check if first condition is already "1 = 1" or "1=1"
    const first = clause.items[0] || '';
    const has1eq1 = /^1\s*=\s*1$/.test(first.trim());
    if (!has1eq1) {
      lines.push('    1 = 1');
      for (const item of clause.items) {
        const upper = item.replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);
        // If item doesn't start with AND/OR, prefix with AND
        if (/^(AND|OR)\s/i.test(upper)) {
          lines.push('    ' + upper);
        } else {
          lines.push('    AND ' + upper);
        }
      }
    } else {
      lines.push('    1 = 1');
      for (let j = 1; j < clause.items.length; j++) {
        const upper = clause.items[j].replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);
        if (/^(AND|OR)\s/i.test(upper)) {
          lines.push('    ' + upper);
        } else {
          lines.push('    AND ' + upper);
        }
      }
    }
    return lines;
  };

  // 9. Process ORDER BY / GROUP BY — comma-separated items
  const processListClause = (clause) => {
    const lines = [];
    const total = clause.items.length;
    clause.items.forEach((item, idx) => {
      const upper = item.replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);
      let line = '    ' + upper;
      if (idx < total - 1) line += ',';
      lines.push(line);
    });
    return lines;
  };

  // 10. Assemble output
  const output = [];
  let joinBuffer = []; // collect JOIN clauses that follow a FROM

  for (let ci = 0; ci < clauses.length; ci++) {
    const clause = clauses[ci];

    if (clause.type === 'SELECT') {
      output.push('SELECT');
      output.push(...processSelectClause(clause));
    } else if (clause.type === 'FROM') {
      // Collect subsequent JOINs
      joinBuffer = [];
      for (let j = ci + 1; j < clauses.length && clauses[j].type === 'JOIN'; j++) {
        joinBuffer.push(clauses[j]);
      }
      output.push('FROM');
      output.push(...processFromClause(clause, joinBuffer));
      // Skip the JOIN clauses we already processed
      ci += joinBuffer.length;
    } else if (clause.type === 'JOIN') {
      // Standalone JOIN (not after FROM) — shouldn't normally happen but handle gracefully
      output.push(...processFromClause({ items: [] }, [clause]));
    } else if (clause.type === 'WHERE') {
      output.push('WHERE');
      output.push(...processWhereClause(clause));
    } else if (clause.type === 'HAVING') {
      output.push('HAVING');
      output.push(...processWhereClause(clause));
    } else if (clause.type === 'ORDER' || clause.type === 'GROUP') {
      output.push(clause.keyword);
      output.push(...processListClause(clause));
    } else {
      // Generic clause (UNION, INSERT INTO, etc.)
      const kw = clause.keyword.replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);
      output.push(kw);
      for (const item of clause.items) {
        const upper = item.replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);
        output.push('    ' + upper);
      }
    }
  }

  // 11. Join, restore placeholders, add trailing semicolon
  let result = output.join('\n');

  // Uppercase keywords one final pass
  result = result.replace(/\b(\w+)\b/g, (w) => SQL_KEYWORDS.has(w.toUpperCase()) ? w.toUpperCase() : w);

  // Restore string literals and comments
  literals.forEach((s, idx) => { result = result.replace(`__LIT${idx}__`, s); });
  existingComments.forEach((c, idx) => { result = result.replace(`__CMT${idx}__`, c); });

  // Trailing semicolon
  result = result.trimEnd();
  if (!result.endsWith(';')) result += ';';

  return result;
}
